'use client';

import { toast, type ToastOptions as ReactToastifyOptions } from 'react-toastify';
import type { ReactNode } from 'react';

import { Toast, ToastVariant, type ToastVariant as ToastVariantName } from './Toast.tsx';

/**
 * The imperative half of the toast family: what a mutation calls when it has
 * something to say.
 *
 *   showSuccessToast(formatted.cartAdded);
 *   showErrorToast(message, { autoClose: false });
 *
 * It crosses into the shared tier with the surface it paints rather than being
 * rebuilt per tenant, because the alternative is every storefront on this
 * platform wiring its own notifier around the same four calls — and a tenant
 * that wired one differently would give its shoppers a different dismissal
 * behaviour for the same event. `react-toastify` reaches for nothing
 * framework-specific, so rule 07 holds.
 *
 * Where a tenant DOES differ is the container: position, stacking and the
 * progress-bar skin are `<Toaster>`'s props and the tenant's own stylesheet.
 */

/**
 * The subset of `react-toastify`'s options this platform offers. Narrow on
 * purpose: everything left out is either the container's decision or something
 * the surface already answers (`closeButton`, `icon`, `className`).
 */
export type ToastOptions = {
  /**
   * Auto close delay in milliseconds
   * @default 5000
   */
  autoClose?: number | false;
  /**
   * Whether to hide the progress bar
   * @default false
   */
  hideProgressBar?: boolean;
  /**
   * Whether to close the toast on click
   * @default true
   */
  closeOnClick?: boolean;
  /**
   * Whether to pause on hover
   * @default true
   */
  pauseOnHover?: boolean;
  /**
   * Whether the toast is draggable
   * @default true
   */
  draggable?: boolean;
};

/**
 * The container renders its own chrome around whatever a toast returns, and
 * this surface paints all of it — so every one of those affordances is turned
 * off and the wrapper is stripped to nothing.
 */
function createToast(variant: ToastVariantName, message: ReactNode, options?: ToastOptions) {
  const toastOptions: ReactToastifyOptions = {
    ...options,
    type: variant,
    closeButton: false,
    icon: false,
    className:
      '!p-0 !m-0 !bg-transparent !min-h-0 !w-auto !max-w-[320px] !rounded-none !border-none overflow-hidden !mb-2',
  };

  return toast(
    ({ closeToast }) => (
      <Toast variant={variant} onClose={closeToast}>
        {message}
      </Toast>
    ),
    toastOptions
  );
}

export function showSuccessToast(message: ReactNode, options?: ToastOptions) {
  return createToast(ToastVariant.SUCCESS, message, options);
}

export function showErrorToast(message: ReactNode, options?: ToastOptions) {
  return createToast(ToastVariant.ERROR, message, options);
}

export function dismissToast(id: ReturnType<typeof showErrorToast>) {
  toast.dismiss(id);
}

export function showWarningToast(message: ReactNode, options?: ToastOptions) {
  return createToast(ToastVariant.WARNING, message, options);
}

/**
 * No caller today. It stays because the four helpers are one API and
 * `ToastVariant.INFO` is one of the four arms `Toast` paints — dropping the
 * only way to produce that arm would leave the icon and colours behind it
 * unreachable, which is a gap rather than a saving.
 */
export function showInfoToast(message: ReactNode, options?: ToastOptions) {
  return createToast(ToastVariant.INFO, message, options);
}
